import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Shield, LogIn, LogOut, Trash2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContacts() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null); // null=checking, object=loaded, false=unauth
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!mounted) return;
        setMe(r.data);
      } catch {
        if (!mounted) return;
        setMe(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!me || !me.is_admin) return;
    setLoading(true);
    axios
      .get(`${API}/admin/contacts`, { withCredentials: true })
      .then((r) => setContacts(r.data.items || []))
      .catch(() => toast.error("FAILED TO LOAD CONTACTS"))
      .finally(() => setLoading(false));
  }, [me]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/contacts";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" +
      encodeURIComponent(redirectUrl);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setMe(false);
    setContacts([]);
    toast.success("SESSION_TERMINATED");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this contact submission? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { withCredentials: true });
      setContacts((c) => c.filter((x) => x.id !== id));
      toast.success("PURGED");
    } catch {
      toast.error("DELETE FAILED");
    }
  };

  // Loading state
  if (me === null) {
    return (
      <Shell>
        <div data-testid="admin-loading" className="font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#A1A1AA]">
          &gt;_ VERIFYING_SESSION<span className="cursor-blink">_</span>
        </div>
      </Shell>
    );
  }

  // Not authenticated
  if (me === false) {
    return (
      <Shell>
        <div className="max-w-md">
          <div className="flex items-center gap-2 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66] mb-6">
            <Shield size={12} strokeWidth={1.5} />
            [AUTH_REQUIRED]
          </div>
          <h1 className="font-['Unbounded'] font-black text-4xl lg:text-6xl leading-none">
            RESTRICTED<span className="text-[#00FF66]">.</span>
          </h1>
          <p className="mt-6 text-[#A1A1AA] leading-relaxed">
            Admin console for RA_SYS contact intake. Access is limited to
            allowlisted Google accounts.
          </p>
          <button
            data-testid="admin-login-google"
            onClick={login}
            className="mt-10 inline-flex items-center gap-3 border border-[#00FF66] text-[#00FF66] px-6 py-3 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] hover:bg-[#00FF66] hover:text-black"
            style={{ transition: "background-color 0.15s, color 0.15s" }}
          >
            <LogIn size={12} strokeWidth={1.5} />
            SIGN IN WITH GOOGLE
          </button>
        </div>
      </Shell>
    );
  }

  // Authenticated but not admin
  if (!me.is_admin) {
    return (
      <Shell>
        <div className="max-w-md">
          <div className="font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#FF3B30] mb-6">
            [FORBIDDEN]
          </div>
          <h1 className="font-['Unbounded'] font-black text-4xl lg:text-6xl leading-none">
            NOT_ON_ALLOWLIST
          </h1>
          <p className="mt-6 text-[#A1A1AA]">
            Signed in as <span className="text-[#F5F5F5]">{me.email}</span>. This
            account is not on the admin allowlist.
          </p>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="mt-10 inline-flex items-center gap-3 border border-white/20 text-[#F5F5F5] px-6 py-3 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] hover:border-[#00FF66] hover:text-[#00FF66]"
            style={{ transition: "border-color 0.15s, color 0.15s" }}
          >
            <LogOut size={12} strokeWidth={1.5} /> SIGN OUT
          </button>
        </div>
      </Shell>
    );
  }

  // Admin view
  return (
    <Shell wide>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <div className="font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#00FF66] mb-3">
            [ADMIN_CONSOLE]
          </div>
          <h1
            data-testid="admin-title"
            className="font-['Unbounded'] font-black text-4xl lg:text-6xl leading-none"
          >
            INTAKE<span className="text-[#00FF66]">.</span>
          </h1>
          <p className="mt-3 text-[#A1A1AA] font-['JetBrains_Mono'] text-[11px] tracking-[0.25em]">
            {me.email} // {contacts.length} SIGNAL(S)
          </p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 border border-white/15 text-[#A1A1AA] px-4 py-2 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] hover:border-[#00FF66] hover:text-[#00FF66]"
            style={{ transition: "border-color 0.15s, color 0.15s" }}
          >
            <ArrowLeft size={12} strokeWidth={1.5} /> SITE
          </Link>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 border border-white/15 text-[#A1A1AA] px-4 py-2 font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] hover:border-[#FF3B30] hover:text-[#FF3B30]"
            style={{ transition: "border-color 0.15s, color 0.15s" }}
          >
            <LogOut size={12} strokeWidth={1.5} /> SIGN OUT
          </button>
        </div>
      </div>

      {loading ? (
        <div className="font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#A1A1AA]">
          &gt;_ FETCHING_INTAKE<span className="cursor-blink">_</span>
        </div>
      ) : contacts.length === 0 ? (
        <div
          data-testid="admin-empty"
          className="border border-white/10 bg-[#0A0A0A] p-10 text-center"
        >
          <div className="font-['JetBrains_Mono'] text-[11px] tracking-[0.3em] text-[#A1A1AA]">
            NO_SIGNALS_YET
          </div>
        </div>
      ) : (
        <ul data-testid="admin-contacts-list" className="border border-white/10 bg-[#0A0A0A] divide-y divide-white/10">
          {contacts.map((c) => (
            <li
              key={c.id}
              data-testid={`admin-contact-row-${c.id}`}
              className="p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 hover:bg-white/[0.02] transition-colors"
            >
              <div className="lg:col-span-3 font-['JetBrains_Mono']">
                <div className="text-[10px] tracking-[0.25em] text-[#00FF66]">
                  {formatDate(c.created_at)}
                </div>
                <div className="mt-1 text-[#F5F5F5] text-sm">{c.name || "—"}</div>
                <a
                  href={`mailto:${c.email}`}
                  className="mt-1 inline-flex items-center gap-2 text-[#A1A1AA] text-xs hover:text-[#00FF66]"
                >
                  <Mail size={11} strokeWidth={1.5} />
                  {c.email}
                </a>
              </div>
              <div className="lg:col-span-8 font-['IBM_Plex_Sans'] text-[#D4D4D8] leading-relaxed text-sm whitespace-pre-wrap break-words">
                {c.brief}
              </div>
              <div className="lg:col-span-1 flex lg:justify-end items-start">
                <button
                  data-testid={`admin-contact-delete-${c.id}`}
                  onClick={() => del(c.id)}
                  aria-label="Delete"
                  className="p-2 border border-white/10 text-[#A1A1AA] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  style={{ transition: "color 0.15s, border-color 0.15s" }}
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Shell>
  );
}

function Shell({ children, wide }) {
  return (
    <main className="min-h-screen bg-[#050505] text-[#F5F5F5]">
      <div className={`mx-auto px-6 lg:px-12 pt-32 pb-24 ${wide ? "max-w-7xl" : "max-w-3xl"}`}>
        {children}
      </div>
    </main>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}.${pad(d.getUTCMonth() + 1)}.${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}Z`;
  } catch {
    return iso;
  }
}
